// Centralized article data - sorted by date (newest first)
// When adding new articles, add them to the top of this array

// Calculate read time based on word count (230 words per minute)
const WORDS_PER_MINUTE = 230;

export const calculateReadTime = (body) => {
  if (!body || !Array.isArray(body)) return '1 min read';
  
  // Join all paragraphs and count words
  const text = body.join(' ');
  const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
  
  // Calculate minutes, round to nearest whole number, minimum 1 minute
  const minutes = Math.max(1, Math.round(wordCount / WORDS_PER_MINUTE));
  
  return `${minutes} min read`;
};

export const articles = [
  {
    id: 'why-alignment-matters',
    category: 'Discovery',
    title: 'Why Alignment Matters More Than Volume',
    excerpt: 'The traditional approach to investor outreach is broken. Here is why quality connections beat quantity every time.',
    date: 'February 2026',
    dateSort: '2026-02',
    body: [
      'Most startup introductions fail long before a first meeting is scheduled. This is often attributed to poor timing, weak decks, or lack of traction. In reality, many conversations fail because the people involved should never have been introduced in the first place.',
      'A growing number of founders and investors are not simply looking for capital. They are looking for alignment. Alignment on risk, on growth expectations, and on values. When alignment is missing, even a great pitch can lead to a polite conversation that goes nowhere.',
      'Many founders unknowingly approach investors whose models depend on structures and expectations that do not match how the founder wants to build. Likewise, many investors find themselves reviewing opportunities that were never designed for partnership in the first place. The result is wasted time on both sides.',
      'High volume outreach creates the illusion of progress. Calendars fill up, decks are refined, conversations happen. Yet very little moves forward. Each misaligned interaction consumes time, energy, and focus that could have been spent building the business or deepening the right relationships.',
      'Quality connections outperform quantity every time because they are rooted in shared intent. When expectations are clear before the first conversation, discussions become more efficient, more honest, and more productive.',
      'Alignment is not a constraint. It is leverage. The earlier it is established, the fewer conversations are needed and the ones that happen matter far more.'
    ],
    disclaimer: 'SeedSynch facilitates introductions only and does not provide financial advice.'
  },
  {
    id: 'pre-pitch-checklist',
    category: 'For Founders',
    title: 'The Pre Pitch Checklist',
    excerpt: 'What founders should clarify before their first investor conversation. Preferences, structure, and expectations.',
    date: 'February 2026',
    dateSort: '2026-02',
    body: [
      'Before speaking to investors, most founders focus on polishing their pitch. Slides, metrics, and storytelling take priority. What often gets overlooked is internal clarity.',
      'Early stage conversations are not won by presentation quality alone. They are shaped by how clearly founders understand their own preferences, constraints, and expectations.',
      'Before the first investor conversation, founders should be able to articulate what kind of capital they are seeking. Equity based partnership, strategic backing, or other forms of support. These choices influence which conversations are worth having at all.',
      'Structure matters as much as vision. Clean ownership, clear roles, and realistic growth assumptions signal seriousness. Investors look for founders who understand not just upside, but downside as well.',
      'Expectations should be explicit. What does success look like in three, five, or ten years. How involved should partners be. What trade offs are unacceptable. These questions are often uncomfortable, but avoiding them delays misalignment rather than preventing it.',
      'A strong pre pitch checklist does not limit opportunity. It filters it. By clarifying intent upfront, founders reduce noise, protect their time, and attract conversations that are structurally viable from the start.'
    ],
    disclaimer: 'SeedSynch facilitates introductions only and does not provide financial advice.'
  },
  {
    id: 'smarter-deal-sourcing',
    category: 'For Investors',
    title: 'Smarter Deal Sourcing',
    excerpt: 'How investors can use preference based matching to find ventures that truly align with their thesis.',
    date: 'January 2026',
    dateSort: '2026-01',
    body: [
      'Deal flow is rarely the problem. Signal quality is.',
      'Many investors are overwhelmed by opportunities that look promising on the surface but fail to align with their actual thesis. Reviewing decks, taking calls, and filtering manually becomes a time intensive process with diminishing returns.',
      'Smarter deal sourcing begins with preference clarity. Stage, sector, geography, and ticket size are only the baseline. More important are expectations around governance, risk sharing, growth pace, and founder mindset.',
      'Preference based matching reduces friction by ensuring that introductions happen only when foundational criteria are met. This does not eliminate judgment or diligence. It improves the starting point.',
      'Misaligned structures are rarely fixable later. Identifying fit early protects time, reputation, and long term outcomes.',
      'Smarter sourcing is not about seeing more opportunities. It is about seeing fewer, better ones, and engaging when alignment is already present.'
    ],
    disclaimer: 'SeedSynch facilitates introductions only and does not provide financial advice.'
  },
  {
    id: 'building-trust-early-stage',
    category: 'Platform',
    title: 'Building Trust in Early Stage Partnerships',
    excerpt: 'Why NDA gated sharing and verified profiles matter for founders and investors alike.',
    date: 'January 2026',
    dateSort: '2026-01',
    body: [
      'Trust is the most fragile asset in early stage partnerships. It cannot be rushed, outsourced, or automated. Yet many platforms treat trust as a by product rather than a design requirement.',
      'At early stages, information asymmetry is unavoidable. What matters is how it is managed. Controlled sharing, clear boundaries, and mutual accountability create conditions where trust can form gradually.',
      'NDA gated access, verified profiles, and intentional introductions are not barriers. They are signals. They indicate seriousness and respect for confidentiality, both of which are critical in early stage collaboration.',
      'Trust also depends on expectations being explicit. When assumptions remain unspoken, disappointment is inevitable. Platforms that encourage clarity before conversation reduce the likelihood of conflict later.',
      'Building trust is not about eliminating risk. It is about making risk visible and shared. Early stage partnerships succeed when trust is designed into the process, not left to chance.'
    ],
    disclaimer: 'SeedSynch facilitates introductions only and does not provide financial advice.'
  },
  {
    id: 'finding-right-cofounder',
    category: 'Co Founders',
    title: 'Finding the Right Co Founder',
    excerpt: 'Skills alignment is just the beginning. Here is what really matters when building a founding team.',
    date: 'December 2025',
    dateSort: '2025-12',
    body: [
      'Skills alignment is often treated as the primary criterion when choosing a co founder. While competence matters, it is rarely the deciding factor in long term success.',
      'Co founders are risk partners. They share decision making authority, reputational exposure, and long term consequences. Misalignment on values, ambition, or resilience can undermine even the strongest technical combination.',
      'Early stage pressure reveals fault lines quickly. How disagreements are handled, how uncertainty is tolerated, and how trade offs are made all stem from underlying alignment rather than resumes.',
      'Co founder alignment is also visible to investors. A fractured founding team signals unresolved risk and weak governance.',
      'Finding the right co founder requires honesty about non negotiables. It requires slowing down early to avoid costly resets later. Skills can be supplemented. Alignment cannot.'
    ],
    disclaimer: 'SeedSynch facilitates introductions only and does not provide financial advice.'
  },
  {
    id: 'hidden-cost-cold-outreach',
    category: 'Discovery',
    title: 'The Hidden Cost of Cold Outreach',
    excerpt: 'Calculating the true time investment of traditional outreach versus curated matchmaking.',
    date: 'December 2025',
    dateSort: '2025-12',
    body: [
      'Cold outreach is often framed as a numbers game. Send enough messages and something will convert. What this framing ignores is cost.',
      'Every cold email, pitch, or call carries an opportunity cost. Time spent researching the wrong investors, tailoring irrelevant decks, and following up on misaligned conversations accumulates quietly.',
      'There is also an emotional cost. Repeated rejection or silence erodes confidence and distorts decision making. Founders may start compromising on structure or expectations simply to keep conversations alive.',
      'Curated matchmaking does not eliminate effort. It redirects it. By focusing on relevance rather than reach, founders and investors preserve energy for conversations that have a real chance of progressing.',
      'The true cost of cold outreach is not failure. It is distraction from building what matters.'
    ],
    disclaimer: 'SeedSynch facilitates introductions only and does not provide financial advice.'
  }
];

// Helper function to get sorted articles (newest first) with calculated read time
export const getSortedArticles = () => {
  return [...articles]
    .sort((a, b) => b.dateSort.localeCompare(a.dateSort))
    .map(article => ({
      ...article,
      readTime: calculateReadTime(article.body)
    }));
};

// Helper function to get homepage articles (5 newest) with calculated read time
export const getHomepageArticles = () => {
  return getSortedArticles().slice(0, 5);
};

// Helper function to get article by ID with calculated read time
export const getArticleById = (id) => {
  const article = articles.find(article => article.id === id);
  if (!article) return null;
  return {
    ...article,
    readTime: calculateReadTime(article.body)
  };
};
